import { useCallback, useContext, useEffect, useState } from 'react'

import {
  Box,
  Button,
  Icon,
  Input,
  Modal,
  Select,
  Text,
} from '@fairfashion/design-system'
import { useTheme } from 'styled-components'

import { AuthContext } from '../../contexts/auth'
import { IFinishedItemCategory } from '../../sdk/finished-item-profile/finished-item-profile.types'
import { sdk } from '../../services/sdk'
import { ConfirmModalContext } from '../ConfirmModal'

import { ProductionCapacityFormModal } from './components/ProductionCapacityFormModal/production-capacity-form-modal.comp'
import {
  ICompanyProductionCapacityForm,
  ProductionCapacityProps,
} from './production-capacity.types'

export const ProductionCapacity = ({ companyId }: ProductionCapacityProps) => {
  const [categories, setCategories] = useState<IFinishedItemCategory[]>([])
  const [open, setOpen] = useState(false)
  const [indexRow, setIndexRow] = useState<number | null>(null)
  const [companyProductionCapacities, setCompanyProductionCapacities] =
    useState<ICompanyProductionCapacityForm[]>([])
  const theme = useTheme()
  const { user } = useContext(AuthContext)
  const { confirmModal } = useContext(ConfirmModalContext)

  const fetchCategories = useCallback(async () => {
    const response = await sdk.finishedItemProfile.getCategories()
    setCategories(response)
  }, [])

  const getCompanyProductionCapacities = useCallback(async () => {
    try {
      const _response =
        await sdk.companyProduction.getCompanyProductionCapacities(companyId)
      setCompanyProductionCapacities(
        _response.map((cpc) => ({
          capacity: cpc.capacity.toString(),
          finishedItemCategoryId: cpc.finishedItemCategoryId,
          taxUnit: cpc.taxUnit,
        })),
      )
    } catch (error) {}
  }, [companyId])

  const handleDeleteCapacity = async (index: number) => {
    if (
      await confirmModal(
        'Excluir capacidade produtiva',
        `Você tem certeza que deseja excluir esta capacidade produtiva? 
        (Os cálculos para os meses anteriores continuarão acessando os dados previamente informados)`,
      )
    ) {
      const finishedItemCategoryId =
        companyProductionCapacities[index].finishedItemCategoryId
      try {
        await sdk.companyProduction.deleteCompanyProductionCapacityCategory(
          companyId,
          finishedItemCategoryId,
        )
        getCompanyProductionCapacities()
      } catch (error) {}
    }
  }

  const getFilteredOptions = (index: number) => {
    const selectedOptions = companyProductionCapacities.map(
      (c) => c.finishedItemCategoryId,
    )
    selectedOptions.splice(index, 1)

    return categories
      .filter((c) => !selectedOptions.includes(c.id))
      .map((c) => ({
        label: c.name,
        value: c.id,
      }))
  }

  const hasCategories = (() => {
    for (const category of categories) {
      const categoryFound = companyProductionCapacities.some(
        (object) => object.finishedItemCategoryId === category.id,
      )

      if (!categoryFound) {
        return false
      }
    }
    return true
  })()

  const handleEdit = (index: number) => {
    setIndexRow(index)
    setOpen(true)
  }
  useEffect(() => {
    !open && setIndexRow(null)
  }, [open])
  useEffect(() => {
    fetchCategories()
    getCompanyProductionCapacities()
  }, [fetchCategories, getCompanyProductionCapacities, open])

  return (
    <>
      {user?.company.id === companyId && (
        <Box width="100%" direction="column" marginTop={3} gap={3}>
          <Box width="100%" justifyContent="space-between" alignItems="center">
            <Text size="large">Capacidade Produtiva</Text>
            <Button
              onClick={() => setOpen(true)}
              padding="10px 14px"
              disabled={hasCategories}
            >
              <Box gap={2} alignItems="center">
                <Icon name="add" color="white" size={20} />
                Adicionar Capacidade
              </Box>
            </Button>
          </Box>
          <Box height="1px" width="100%" backgroundColor="grey_4" />
        </Box>
      )}
      <Box width="100%" paddingY={2} gap={2} direction="column">
        {companyProductionCapacities.map((item, index) => (
          <Box
            key={index}
            width="100%"
            border={{ width: 1, color: 'grey_4' }}
            borderRadius={theme.border_radius.card}
            boxShadow={theme.box_shadow.card}
            padding={2}
            gap={2}
            alignItems="center"
            flexWrap="wrap"
          >
            <Box width="300px">
              <Select
                label="Categoria"
                value={`${item.finishedItemCategoryId}`}
                options={getFilteredOptions(index)}
                disabled
              />
            </Box>
            <Box minWidth="140px">
              <Input label="Capacidade" value={`${item.capacity}`} disabled />
            </Box>
            <Box minWidth="140px">
              <Input label="Unidade de medida" value={item.taxUnit} disabled />
            </Box>
            {user?.company.id === companyId && (
              <Box flex={1} justifyContent="flex-end" gap={2}>
                <Button
                  variant="text"
                  padding="0"
                  onClick={() => handleEdit(index)}
                >
                  <Icon name="edit" size={20} color="grey_3" />
                </Button>
                <Button
                  variant="text"
                  padding="0"
                  onClick={() => handleDeleteCapacity(index)}
                >
                  <Icon name="delete_bin7" size={20} color="grey_3" />
                </Button>
              </Box>
            )}
          </Box>
        ))}
        {companyProductionCapacities.length === 0 && (
          <Box width="100%" padding={5} justifyContent="center">
            <Text align="center">Não há dados</Text>
          </Box>
        )}
      </Box>

      <Modal
        open={open}
        onClose={() => setOpen(false)}
        customTitle={
          <Text textTransform="uppercase" size="small" color="grey_3">
            Adicionar Capacidade Produtiva
          </Text>
        }
        width="100%"
        maxWidth="384px"
      >
        {open && (
          <ProductionCapacityFormModal
            companyId={companyId}
            index={indexRow ?? companyProductionCapacities.length}
            setModalOpen={setOpen}
            categories={categories}
            capacitiesData={companyProductionCapacities}
            getCompanyProductionCapacities={getCompanyProductionCapacities}
          />
        )}
      </Modal>
    </>
  )
}
